import { Link } from "react-router-dom";
import { BookOpen, GraduationCap, Building2, Check, Minus, ArrowRight } from "lucide-react";

const COURSE_TEXT = {
  meets: (o) => `Your best three predicted A-levels meet the published typical offer (${o}).`,
  close: (o) => `You're one grade below the published typical offer (${o}).`,
  below: (o) => `You're more than one grade below the published typical offer (${o}).`,
  unknown: () => "Add predicted A-level grades to compare them with the published offer.",
};

function Card({ icon: Icon, title, question, children, cta, testId }) {
  return (
    <div data-testid={testId} className="flex flex-col rounded-2xl border border-stone-200 bg-white p-5 transition-shadow duration-200 hover:shadow-md">
      <div className="flex items-center gap-2 text-[#002147]"><Icon className="h-4 w-4" aria-hidden="true" />
        <span className="font-mono text-[10px] uppercase tracking-[0.18em]">{title}</span></div>
      <p className="mt-2 font-editorial text-xl text-stone-900">{question}</p>
      <div className="mt-3 flex-1 space-y-2 text-sm text-stone-700">{children}</div>
      {cta}
    </div>
  );
}

const Cta = ({ to, children, testId }) => (
  <Link to={to} data-testid={testId} className="mt-4 inline-flex min-h-[40px] items-center gap-1 text-sm font-medium text-[#002147] underline-offset-4 hover:underline">
    {children} <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
  </Link>
);

const Signal = ({ s }) => (
  <p className="flex gap-2">{s.kind === "match" ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" /> : <Minus className="mt-0.5 h-4 w-4 shrink-0 text-stone-400" />}<span>{s.text}</span></p>
);

export function SubjectFitCard({ fit }) {
  const signals = fit?.signals || [];
  return (
    <Card icon={BookOpen} title="Subject fit" question="Is Philosophy for you?" testId="sci-fit-subject"
      cta={<Cta to="/realisations" testId="sci-fit-subject-realisations">Reflect in Realisations</Cta>}>
      {!fit?.has_profile && <p>Add your interests and grades in your Profile to see signals here.</p>}
      {fit?.has_profile && !signals.length && <p>Nothing in your profile points towards or away from Philosophy yet.</p>}
      {signals.map((s, i) => <Signal key={i} s={s} />)}
    </Card>
  );
}

export function CourseFitCard({ fit }) {
  const text = fit?.status === "not_comparable" ? fit.note : (COURSE_TEXT[fit?.status] || COURSE_TEXT.unknown)(fit?.offer_compared || fit?.offer);
  return (
    <Card icon={GraduationCap} title="Course fit" question="Is this course for you?" testId="sci-fit-course"
      cta={<Cta to="/beta/course-fit" testId="sci-fit-course-fast-match">Run Fast Match</Cta>}>
      {!fit?.has_profile ? <p>Add predicted grades in your Profile to compare them with the offer.</p> : <p data-testid="sci-fit-course-status">{text}</p>}
      <p className="text-xs text-stone-500">A simple grade comparison, not a prediction of your chances.</p>
    </Card>
  );
}

export function UniversityFitCard({ fit, uniId }) {
  return (
    <Card icon={Building2} title="University fit" question="Is this university for you?" testId="sci-fit-university"
      cta={<Cta to={`/uni/${uniId}`} testId="sci-fit-university-link">See the university</Cta>}>
      <p>{fit.city}, {fit.country}.{fit.country_preferred === true && " It's in a country you prefer."}{fit.country_preferred === false && " It's not in your preferred countries."}</p>
      <p>City life, cost and campus are about the university, not the subject. Compare them in Place.</p>
      {fit.city_slug && <Link to={`/cities/${fit.city_slug}`} data-testid="sci-fit-university-city" className="text-sm text-[#002147] underline underline-offset-2">City guide</Link>}
    </Card>
  );
}

export const ProfileLink = () => (
  <Link to="/profile" data-testid="sci-profile-link" className="text-sm font-medium text-[#002147] underline underline-offset-2">Update your Profile</Link>
);
